import { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitAdRequest } from '../services/api';
import useSeo from '../hooks/useSeo';

const MAX_ATTACHMENTS = 5;

function playSubmitSuccessBeep() {
  try {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = new AudioContextCtor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.14);

    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.19);

    osc.onended = () => {
      ctx.close().catch(() => {});
    };
  } catch (_) {
    // Non-fatal: sound feedback should never block form submission UX.
  }
}

export default function AdvertisePage() {
  useSeo({
    title: 'Advertise With Us',
    description: 'Submit your ad request for privacy-first, contextual advertising on TheScandal.',
    path: '/advertise',
    keywords: [
      'advertise on thescandal',
      'contextual advertising',
      'privacy friendly ads',
      'sponsored post request',
      'banner ad request',
    ],
  });

  const [form, setForm] = useState({
    businessName: '',
    email: '',
    adType: 'banner',
    description: '',
    keywords: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      if (!termsAccepted) {
        throw new Error('Please accept the advertising terms and conditions to continue.');
      }

      const fd = new FormData();
      fd.append('businessName', form.businessName.trim());
      fd.append('email', form.email.trim());
      fd.append('adType', form.adType);
      fd.append('description', form.description.trim());
      fd.append('keywords', form.keywords);
      fd.append('termsAccepted', String(termsAccepted));
      attachments.forEach((file) => fd.append('attachments', file));

      const result = await submitAdRequest(fd, {
        onProgress: (pct) => setUploadProgress(pct),
      });
      setSuccess(`Request sent. Reference ID: ${result.requestId}`);
      playSubmitSuccessBeep();
      setForm({ businessName: '', email: '', adType: 'banner', description: '', keywords: '' });
      setAttachments([]);
      setTermsAccepted(false);
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  }

  function handleAttachmentChange(event) {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    const next = [...attachments, ...picked].slice(0, MAX_ATTACHMENTS);
    setAttachments(next);
    event.target.value = '';
  }

  function removeAttachment(idx) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <section className="advertise-page">
      <header className="advertise-header">
        <h1>Advertise With Us</h1>
        <p>Reach engaged readers with privacy-friendly contextual ads.</p>
      </header>

      <form className="advertise-form" onSubmit={onSubmit}>
        <label className="form-label">
          Business Name
          <input
            className="form-input"
            value={form.businessName}
            onChange={(e) => updateField('businessName', e.target.value)}
            required
            maxLength={200}
          />
        </label>

        <label className="form-label">
          Email
          <input
            className="form-input"
            type="email"
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            required
            maxLength={200}
          />
        </label>

        <label className="form-label">
          Ad Type
          <select
            className="form-input"
            value={form.adType}
            onChange={(e) => updateField('adType', e.target.value)}
          >
            <option value="banner">Banner</option>
            <option value="sponsoredPost">Sponsored Post</option>
          </select>
        </label>

        <label className="form-label">
          Campaign Description
          <textarea
            className="form-input"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            required
            maxLength={2500}
            rows={5}
            placeholder="Tell us what you want to promote and your target audience"
          />
        </label>

        <label className="form-label">
          Keywords (comma separated)
          <input
            className="form-input"
            value={form.keywords}
            onChange={(e) => updateField('keywords', e.target.value)}
            placeholder="security, banking, telecom, jobs"
          />
        </label>

        <label className="form-label">
          Attachments (optional, up to {MAX_ATTACHMENTS})
          <input
            className="form-input"
            type="file"
            multiple
            onChange={handleAttachmentChange}
            accept=".jpg,.jpeg,.png,.mp4,.webm,.mov,.pdf,.doc,.docx"
          />
        </label>

        {attachments.length > 0 && (
          <div className="comment-preview-grid">
            {attachments.map((file, idx) => (
              <div className="preview-item" key={`${file.name}-${idx}`}>
                <div className="preview-file-name" title={file.name}>{file.name}</div>
                <button
                  type="button"
                  className="preview-remove"
                  onClick={() => removeAttachment(idx)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="terms-check">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <span>
            I accept the advertising terms and conditions: submitted materials must be lawful,
            non-deceptive, and suitable for public display. TheScandal may approve, reject,
            pause, or remove ads at its discretion.
          </span>
        </label>

        {error && <p className="form-error">{error}</p>}
        {success && <p className="success-text">{success}</p>}

        {submitting && attachments.length > 0 && (
          <div className="upload-progress" aria-live="polite">
            <div className="upload-progress-track">
              <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <span className="upload-progress-label">Uploading attachments... {uploadProgress}%</span>
          </div>
        )}

        <div className="form-actions">
          <Link to="/" className="btn-ghost btn-link advertise-back-btn">Back to Feed</Link>
          <button className="btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </form>
    </section>
  );
}
